import type React from "react";

interface ScribaIconProps {
  className?: string;
  size?: number;
}

/**
 * ScribaIcon - The main logo icon for the Scriba application
 * Renders the original quill/calligraphy SVG as a React component for immediate rendering
 */
export const ScribaIcon: React.FC<ScribaIconProps> = ({ className = "", size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    version="1.1"
    width={size}
    height={size}
    viewBox="0 0 1080 1080"
    xmlSpace="preserve"
    className={className}
    role="img"
    aria-label="Scriba Logo"
  >
    <g transform="matrix(2.95 2.95 -3.07 3.07 635.18 286.45)">
      <polygon
        style={{
          stroke: "rgb(0,0,0)",
          strokeWidth: 0,
          strokeDasharray: "none",
          strokeLinecap: "butt",
          strokeDashoffset: 0,
          strokeLinejoin: "miter",
          strokeMiterlimit: 4,
          fill: "currentColor",
          fillRule: "nonzero",
          opacity: 1,
        }}
        vectorEffect="non-scaling-stroke"
        points="0,-35.47 11.56,-12.17 37.29,-8.4 18.68,9.8 23.06,35.47 0,23.42 -23.06,35.47 -18.74,9.8 -37.29,-8.4 -11.56,-12.17"
      />
    </g>
    <g transform="matrix(3.23 -1.06 1.06 3.23 712.73 571.06)">
      <polygon
        style={{
          stroke: "rgb(0,0,0)",
          strokeWidth: 0,
          strokeDasharray: "none",
          strokeLinecap: "butt",
          strokeDashoffset: 0,
          strokeLinejoin: "miter",
          strokeMiterlimit: 4,
          fill: "currentColor",
          fillRule: "nonzero",
          opacity: 1,
        }}
        vectorEffect="non-scaling-stroke"
        points="0,-35.47 11.56,-12.17 37.29,-8.4 18.68,9.8 23.06,35.47 0,23.42 -23.06,35.47 -18.74,9.8 -37.29,-8.4 -11.56,-12.17"
      />
    </g>
    <g transform="matrix(-0.48 -3.52 3.52 -0.48 839.75 838.61)">
      <polygon
        style={{
          stroke: "rgb(0,0,0)",
          strokeWidth: 0,
          strokeDasharray: "none",
          strokeLinecap: "butt",
          strokeDashoffset: 0,
          strokeLinejoin: "miter",
          strokeMiterlimit: 4,
          fill: "currentColor",
          fillRule: "nonzero",
          opacity: 1,
        }}
        vectorEffect="non-scaling-stroke"
        points="0,-35.47 11.56,-12.17 37.29,-8.4 18.68,9.8 23.06,35.47 0,23.42 -23.06,35.47 -18.74,9.8 -37.29,-8.4 -11.56,-12.17"
      />
    </g>
    <g transform="matrix(1.73 0 0 1.73 371.01 590.57)">
      <path
        style={{
          stroke: "none",
          strokeWidth: 1,
          strokeDasharray: "none",
          strokeLinecap: "butt",
          strokeDashoffset: 0,
          strokeLinejoin: "miter",
          strokeMiterlimit: 4,
          fill: "currentColor",
          fillRule: "nonzero",
          opacity: 1,
        }}
        vectorEffect="non-scaling-stroke"
        transform="translate(-421.02, -502.86)"
        d="M 308.044495 277.90564 C 309.553436 278.989014 310.777649 279.855469 311.986328 280.743073 C 326.25293 291.220215 326.220795 291.226624 329.312347 308.701508 C 330.180939 313.611115 331.270599 318.482452 332.302002 323.362213 C 333.315582 328.15741 334.383881 332.94104 335.833466 337.68985 C 335.833466 325.125275 335.833466 312.560699 335.833466 299.526428 C 340.509155 300.903961 342.812866 304.52063 346.18869 306.40863 C 356.842987 312.367218 359.567566 321.327576 358.525787 333.055115 C 357.512512 344.461731 358.311005 356.029327 358.325623 367.526245 C 358.325745 367.624847 358.487488 367.723267 358.984497 368.290558 C 362.792206 354.457367 366.511383 340.945831 370.387054 326.865784 C 376.387024 330.52002 380.953583 334.848846 385.599335 338.996948 C 424.586609 373.807373 460.631989 411.404419 492.924866 452.553619 C 495.320343 455.606018 496.543793 458.645142 496.319 462.578094 C 495.55899 475.875671 495.012207 489.185852 494.450989 502.494049 C 494.313202 505.761322 494.43042 509.039337 494.907471 512.403381 C 499.909454 499.694733 501.695618 486.03006 506.990234 471.951416 C 536.842224 512.315125 555.005615 555.024902 550.400452 605.468811 C 557.100647 595.517517 561.493774 584.828308 566.57428 574.510742 C 567.042175 574.572998 567.510071 574.635254 567.977966 574.69751 C 568.31897 577.523499 568.923157 580.346069 568.960449 583.176147 C 569.340332 612.02771 567.999084 640.77063 562.08844 669.096008 C 561.108459 673.791992 562.220215 676.886841 565.052734 680.370544 C 584.922424 704.807983 604.32428 729.604309 621.294373 756.195618 C 622.546204 758.157104 623.789185 760.158813 624.723877 762.27948 C 625.901001 764.950195 625.527954 767.570435 623.239746 769.567627 C 620.856262 771.647766 618.055847 772.05072 615.405273 770.286987 C 613.206421 768.823853 611.191956 767.021179 609.311157 765.152222 C 588.711487 744.681641 570.149414 722.421753 552.031067 699.774414 C 551.002808 698.489136 549.919617 697.247803 548.40094 695.435913 C 534.060791 707.66748 518.849915 718.364746 503.276764 728.53949 C 502.991272 728.21759 502.70578 727.895752 502.420319 727.573853 C 512.69165 715.540405 522.963013 703.506897 534.476135 690.018616 C 492.009216 686.401733 453.67453 674.20874 417.779266 654.489563 C 417.837341 654.051514 417.895416 653.613403 417.953491 653.175293 C 441.927124 653.175293 465.900726 653.175293 489.874359 653.175293 C 489.952148 652.767212 490.029938 652.359192 490.107727 651.951111 C 471.223877 645.740723 452.871429 638.361816 435.972748 627.817566 C 418.945007 617.192749 403.862701 604.311096 389.746674 588.231812 C 402.090485 587.21228 413.257294 589.702026 424.544678 587.495667 C 414.673706 585.119873 404.807007 582.726013 394.92981 580.376343 C 390.401672 579.299072 385.865326 578.248413 381.307648 577.307007 C 379.375702 576.907898 377.970947 575.856689 376.786682 574.416077 C 365.355774 560.511353 354.341705 546.285461 343.91391 531.610596 C 343.55545 531.106201 343.43338 530.433777 342.845154 528.929138 C 357.577423 531.171448 371.426514 534.746216 385.672424 536.008728 C 377.3573 531.032959 368.151093 528.204224 359.320892 524.527893 C 351.175018 521.136536 342.957977 517.911194 334.719849 514.749329 C 332.277771 513.812073 330.398895 512.455383 328.943207 510.273468 C 305.239655 474.745636 284.106171 437.735565 265.013428 399.547546 C 264.514771 398.55011 264.283661 397.418915 263.615601 395.412476 C 272.735626 397.838531 281.121155 400.069183 289.506683 402.299835 C 289.623291 402.048706 289.739868 401.797546 289.856476 401.546417 C 280.660919 396.869934 271.502136 392.118713 262.244476 387.568665 C 259.880096 386.406555 258.110138 385.011353 256.987885 382.583527 C 240.88768 347.753113 226.544708 312.299561 219.235947 274.419952 C 217.317261 264.475891 215.476669 254.443665 217.614578 244.215378 C 219.343155 235.94548 223.095184 233.145981 231.454453 234.770966 C 238.195572 236.081345 244.432709 238.950134 250.529022 242.056732 C 270.623993 252.296829 289.342407 264.75119 308.044495 277.90564 z"
        strokeLinecap="round"
      />
    </g>
    <g transform="matrix(3.35 -0.56 0.56 3.35 942.89 597.25)">
      <polygon
        style={{
          stroke: "rgb(0,0,0)",
          strokeWidth: 0,
          strokeDasharray: "none",
          strokeLinecap: "butt",
          strokeDashoffset: 0,
          strokeLinejoin: "miter",
          strokeMiterlimit: 4,
          fill: "currentColor",
          fillRule: "nonzero",
          opacity: 1,
        }}
        vectorEffect="non-scaling-stroke"
        points="0,-35.47 11.56,-12.17 37.29,-8.4 18.68,9.8 23.06,35.47 0,23.42 -23.06,35.47 -18.74,9.8 -37.29,-8.4 -11.56,-12.17"
      />
    </g>
    <g transform="matrix(1.76 2.91 -2.91 1.76 902.32 332.73)">
      <polygon
        style={{
          stroke: "rgb(0,0,0)",
          strokeWidth: 0,
          strokeDasharray: "none",
          strokeLinecap: "butt",
          strokeDashoffset: 0,
          strokeLinejoin: "miter",
          strokeMiterlimit: 4,
          fill: "currentColor",
          fillRule: "nonzero",
          opacity: 1,
        }}
        vectorEffect="non-scaling-stroke"
        points="0,-35.47 11.56,-12.17 37.29,-8.4 18.68,9.8 23.06,35.47 0,23.42 -23.06,35.47 -18.74,9.8 -37.29,-8.4 -11.56,-12.17"
      />
    </g>
  </svg>
);
